import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import StaffCard from "../components/StaffCard";
import StaffOnboardingWizard from "../components/StaffOnboardingWizard";
import { thisMonth, thisYear, FULL_MONTHS } from "../lib/utils";
import { UserPlus } from "lucide-react";

const PRIVILEGED_ROLES = ["CEO", "Project Manager", "Administration"];

export default function StaffDirectoryPage() {
  const { user, hasCapability } = useAuth();
  const hmHomes = (user.assigned_homes && user.assigned_homes.length ? user.assigned_homes : (user.assigned_home ? [user.assigned_home] : []));
  const isMultiHomeHM = user.access_role === "House Manager" && hmHomes.length > 1;
  const [staff, setStaff] = useState([]);
  const [homes, setHomes] = useState([]);
  const [home, setHome] = useState(user.access_role === "House Manager" ? (isMultiHomeHM ? "" : (hmHomes[0] || "")) : "");
  const [classification, setClassification] = useState("");
  const [showFormer, setShowFormer] = useState(false);
  const canSeeFormer = ["CEO", "Project Manager", "COO", "Administration", "House Manager"].includes(user.access_role);
  const [summaryMap, setSummaryMap] = useState({});
  const [showWizard, setShowWizard] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const month = thisMonth(); const year = thisYear();
  // CEO/PM/Admin always; HM only when their permission matrix grants User Management → Create.
  const canOnboard = PRIVILEGED_ROLES.includes(user.access_role)
                  || (user.access_role === "House Manager" && hasCapability("User Management", "Create"));

  useEffect(() => { api.get("/homes").then(({ data }) => setHomes(data)); },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  useEffect(() => {
    const params = {};
    if (home) params.home = home;
    if (classification) params.classification = classification;
    if (showFormer) params.status = "former";
    api.get("/staff", { params }).then(({ data }) => setStaff(data));
    api.get("/summary", { params: { ...(home ? { home } : {}), month, year } }).then(({ data }) => {
      const map = {};
      data.forEach((r) => { map[r.staff_user_id] = r; });
      setSummaryMap(map);
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [home, classification, month, year, reloadTick, showFormer]);

  const summary = useMemo(() => {
    const direct = staff.filter(s => s.staff_classification === "Direct Care Worker").length;
    const contract = staff.filter(s => s.staff_classification === "Contract Care Worker").length;
    return { direct, contract };
  }, [staff]);

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-small-caps">Roster</p>
          <h1 className="text-h1 mt-1">Staff Directory</h1>
          <p className="text-sm text-slate-500 mt-1">{staff.length} staff · {FULL_MONTHS[month - 1]} {year}</p>
        </div>
        {canOnboard && (
          <button onClick={() => setShowWizard(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
            data-testid="staff-directory-onboard-btn">
            <UserPlus size={14} /> Onboard New Staff
          </button>
        )}
      </div>

      <div className="nv-card p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="staff-filters">
          {(user.access_role !== "House Manager" || isMultiHomeHM) && (
            <div>
              <label className="text-small-caps">Home</label>
              <select value={home} onChange={(e) => setHome(e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="staff-home-filter">
                <option value="">All Homes</option>
                {homes.map((h) => <option key={h.home_name} value={h.home_name}>{h.home_name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-small-caps">Classification</label>
            <select value={classification} onChange={(e) => setClassification(e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="staff-classification-filter">
              <option value="">All</option>
              <option>Direct Care Worker</option>
              <option>Contract Care Worker</option>
              <option>House Manager</option>
              <option>Administration</option>
            </select>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-2.5 py-1 font-medium">Direct Care Worker: {summary.direct}</span>
          <span className="rounded-full bg-purple-50 text-purple-700 border border-purple-200 px-2.5 py-1 font-medium">Contract Care Worker: {summary.contract}</span>
          {canSeeFormer && (
            <label className="ml-auto inline-flex items-center gap-1.5 text-slate-600 cursor-pointer">
              <input type="checkbox" checked={showFormer} onChange={(e) => setShowFormer(e.target.checked)} data-testid="staff-show-former-toggle" />
              Show Former Staff only
            </label>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="staff-grid">
        {staff.map((s) => {
          const summ = summaryMap[s.user_id];
          return <StaffCard key={s.id} staff={s} score={summ?.overall_weighted_score} band={summ?.performance_band} />;
        })}
        {staff.length === 0 && <div className="col-span-full nv-card p-10 text-center text-slate-500">No staff match the current filters.</div>}
      </div>

      {showWizard && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
             onClick={() => setShowWizard(false)} data-testid="staff-directory-onboard-modal">
          <div className="nv-card w-full max-w-2xl max-h-[92vh] overflow-y-auto p-6"
               onClick={(e) => e.stopPropagation()}>
            <h2 className="text-h2 inline-flex items-center gap-2">
              <UserPlus size={18} className="text-emerald-700" /> Onboard New Staff
            </h2>
            <p className="text-sm text-slate-500 mt-1 mb-5">4-step wizard · creates user, links staff record, and sends welcome email.</p>
            <StaffOnboardingWizard
              onClose={() => setShowWizard(false)}
              onCreated={() => setReloadTick((t) => t + 1)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
